import { Skeleton } from "@mantine/core";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { useListTemplates } from "../../api/generated/endpoints/generation/generation";
import { useListBrands, useListMyTeams } from "../../api/generated/endpoints/teams/teams";
import { freshUrls } from "../../api/signed-urls";
import type { TemplateCard } from "../../api/generated/model";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { ContinueSection } from "./ContinueSection";
import classes from "./home2.module.css";
import { SectionHead } from "./SectionHead";
import { SoonControl } from "./Soon";
import shell from "./studio2.module.css";
import { coverOf } from "./templates/cover";

/** The prototype's four, in its order; the list's first others fill any it lacks (decision 11). */
const FEATURED = ["Podcast Ad", "Promote Your Product", "Claymation", "Talking Head Overlay"];
// The read the Templates page's My Generations makes for its titles, so the two share it.
const EVERY = 100;

export function featuredTemplates(list: readonly TemplateCard[]): TemplateCard[] {
  const open = list.filter((each) => !each.coming_soon);
  const named = FEATURED.flatMap((title) => {
    const found = open.find((each) => each.title.toLowerCase() === title.toLowerCase());
    return found === undefined ? [] : [found];
  });
  return [...named, ...open.filter((each) => !named.includes(each))].slice(0, 4);
}

function HomeTemplates({ teamId, brandId }: { teamId: string; brandId: string }) {
  // The covers are signed URLs: the list is read again before they expire, as the dialog's.
  const templates = useListTemplates(
    teamId,
    { limit: EVERY },
    { query: freshUrls((page) => page.urls_expire_at) },
  );
  const page = templates.data;
  // The whole catalogue fits one read, so its length is the count, as the prototype's "All 15".
  const whole = page !== undefined && page.next === null;
  const all = whole ? `All ${page.items.length} templates` : "All templates";
  return (
    <section aria-labelledby="home2-templates">
      <SectionHead title="Templates" id="home2-templates">
        <Link
          to="/teams/$teamId/brands/$brandId/home2/templates"
          params={{ teamId, brandId }}
          className={classes.subtle}
        >
          {all} <ArrowRight size={14} aria-hidden />
        </Link>
      </SectionHead>
      {templates.isError ? <ErrorAlert error={templates.error} /> : null}
      <div className={classes.templates}>
        {page === undefined
          ? [0, 1, 2, 3].map((each) => <Skeleton key={each} className={classes.templateSkeleton} />)
          : featuredTemplates(page.items).map((template) => {
              const cover = coverOf(template);
              return (
                // The template's preview in Pro (spec ui-perf/ui-switch-classic-simple-pro, e2).
                <Link
                  key={template.id}
                  to="/teams/$teamId/brands/$brandId/home2/templates"
                  params={{ teamId, brandId }}
                  search={{ template: template.id }}
                  className={classes.templateCard}
                  aria-label={template.title}
                >
                  <span className={classes.thumb}>
                    {cover === null ? null : (
                      <img src={cover} alt="" loading="lazy" decoding="async" />
                    )}
                  </span>
                  <b className={classes.templateTitle}>{template.title}</b>
                  <span className={classes.tiny}>{template.description}</span>
                </Link>
              );
            })}
      </div>
    </section>
  );
}

/** The prototype's three other ways to start, beside "Start here". */
const STARTS = [
  ["Image ad", "Your product with a headline and a button, or clean product shots."],
  ["Product video", "Your product photo turned into a short clip."],
  ["Storyboard ad", "Script, cast and shots, built into one video."],
] as const;

function StartRow({ teamId, brandId }: { teamId: string; brandId: string }) {
  return (
    <div className={classes.starts}>
      {/* The prototype's Start here opens the Talking head page (PR c3). */}
      <Link
        to="/teams/$teamId/brands/$brandId/home2/create-talking"
        params={{ teamId, brandId }}
        className={classes.startCard}
      >
        <span className={classes.startTag}>Start here</span>
        <span className={classes.startTitle}>UGC video ad</span>
        <span className={classes.startText}>
          An AI actor talks about your product in your script and voice. About 2 minutes to a
          finished ad.
        </span>
        <span className={classes.startCta}>
          Start <ArrowRight size={16} aria-hidden />
        </span>
      </Link>
      {STARTS.map(([title, text]) => {
        const body = (
          <>
            <span className={classes.jobTitle}>{title}</span>
            <span className={classes.small}>{text}</span>
          </>
        );
        // The prototype's Image ad opens the image page on Ad static (PR c1), its Product video
        // the video page (PR c2).
        return title === "Image ad" ? (
          <Link
            key={title}
            to="/teams/$teamId/brands/$brandId/home2/create-image"
            params={{ teamId, brandId }}
            search={{ mode: "ad-static" }}
            className={classes.jobCard}
          >
            {body}
          </Link>
        ) : title === "Product video" ? (
          <Link
            key={title}
            to="/teams/$teamId/brands/$brandId/home2/create-video"
            params={{ teamId, brandId }}
            className={classes.jobCard}
          >
            {body}
          </Link>
        ) : (
          <SoonControl key={title} block className={classes.jobCard} label={title}>
            {body}
          </SoonControl>
        );
      })}
    </div>
  );
}

/**
 * Home 2 (spec ui-perf/pro-studio-redesign, PR b): the brand and team over "What are we making
 * today?", four templates, the ways to start, the user's latest work, and the brand's products.
 */
export function Home2Screen() {
  const { teamId, brandId } = useParams({
    from: "/signed-in/teams/$teamId/brands/$brandId/home2/",
  });
  const same = (id: string, other: string) => id.toLowerCase() === other.toLowerCase();
  const brand = useListBrands(teamId, {
    query: { select: (list) => list.find((each) => same(each.id, brandId)) },
  }).data;
  const team = useListMyTeams({
    query: { select: (list) => list.find((each) => same(each.id, teamId)) },
  }).data;
  const eyebrow = [brand?.name, team?.name].filter(Boolean).join(" · ");
  return (
    <div className={`${shell.page} ${classes.home}`}>
      <section className={shell.hero}>
        <p className={shell.eyebrow}>{eyebrow}</p>
        <h1 className={shell.heroTitle}>
          What are we <span className={shell.gradient}>making</span> today?
        </h1>
      </section>
      <HomeTemplates teamId={teamId} brandId={brandId} />
      <StartRow teamId={teamId} brandId={brandId} />
      <ContinueSection teamId={teamId} brandId={brandId} />
      {/* No "Your products": the Products page has them (Dovydas, spec ui-switch decision 17). */}
    </div>
  );
}
